import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Layout from '../components/Layout';
import {
  CheckCircle,
  AlertCircle,
  Clock,
  CreditCard,
  Zap,
  Users,
  Database,
  Receipt,
  ExternalLink,
  Ban,
  GraduationCap,
  Upload,
  X,
} from 'lucide-react';

const SubscriptionPage = () => {
  const { user, reloadAuthStatus } = useAuth();
  const { showToast } = useToast();

  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [billingType, setBillingType] = useState('monthly');
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [activatingPayment, setActivatingPayment] = useState(false);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [billingChangeConfirmation, setBillingChangeConfirmation] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponValidation, setCouponValidation] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  const [myBenefit, setMyBenefit] = useState(null);
  const [showBenefitModal, setShowBenefitModal] = useState(false);
  const [benefitSubmitting, setBenefitSubmitting] = useState(false);
  const [benefitForm, setBenefitForm] = useState({
    subtype: 'new_graduate',
    institution: '',
    course: '',
    specialty: '',
    expected_end_date: '',
    graduation_date: '',
    notes: '',
    proof_document: null,
  });
  const [showAcademicActivationModal, setShowAcademicActivationModal] = useState(false);

  // Billing Fields
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [billingPhone, setBillingPhone] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [address, setAddress] = useState('');
  const [addressNumber, setAddressNumber] = useState('');
  const [complement, setComplement] = useState('');
  const [province, setProvince] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');

  // Auto-fill from user
  useEffect(() => {
    if (user?.billing_info && !cpfCnpj) {
      setCpfCnpj(user.billing_info.cpf_cnpj || '');
      setBillingPhone(user.billing_info.billing_phone || user.billing_phone || user.phone || '');
      setPostalCode(user.billing_info.postal_code || '');
      setAddress(user.billing_info.address || '');
      setAddressNumber(user.billing_info.address_number || '');
      setComplement(user.billing_info.complement || '');
      setProvince(user.billing_info.province || '');
      setCity(user.billing_info.city || '');
      setState(user.billing_info.state || '');
    }
  }, [user, cpfCnpj]);


  const handleCepBlur = async () => {
    const cep = postalCode.replace(/\D/g, '');
    if (cep.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (!data.erro) {
         setAddress(data.logradouro || '');
         setProvince(data.bairro || '');
         setCity(data.localidade || '');
         setState(data.uf || '');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'http://localhost:8001'
      : '');


  useEffect(() => {
    fetchPlans();
    fetchCurrentSubscription();
    fetchMyBenefit();
  }, []);


  const singlePlanMode = plans.length === 1;

  const currentEffectiveMonthlyPrice =
    currentSubscription?.effective_monthly_price ??
    currentSubscription?.contracted_monthly_price ??
    plans[0]?.price_monthly;

  const currentEffectiveYearlyPrice =
    currentSubscription?.effective_yearly_price ??
    currentSubscription?.contracted_yearly_price ??
    plans[0]?.price_yearly;

  const hasFounderPrice =
    currentSubscription?.launch_discount_lifetime === true ||
    currentSubscription?.contracted_price_origin === 'launch_offer';

  const isBillingCycleChange = Boolean(
    currentSubscription?.billing_type &&
    currentSubscription.billing_type !== billingType
  );

  const isPaymentMethodChange = Boolean(
    currentSubscription?.payment_method &&
    currentSubscription.payment_method !== paymentMethod
  );

  const currentPlanDisplayName = useMemo(() => {
    if (!currentSubscription?.plan_name) return 'Plano OdontoAssist AI';
    return currentSubscription.plan_name;
  }, [currentSubscription]);

  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/subscriptions/plans`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPlans(Array.isArray(data) ? data : []);
      } else {
        showToast('Erro ao carregar planos', 'error');
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
      showToast('Erro ao carregar planos', 'error');
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/subscriptions/user`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();

        setCurrentSubscription(data);

        if (['monthly', 'yearly'].includes(data?.billing_type)) {
          setBillingType(data.billing_type);
        }
        if (['credit_card', 'boleto'].includes(data?.payment_method)) {
          setPaymentMethod(data.payment_method);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar assinatura:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyBenefit = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/benefits/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMyBenefit(data);
      }
    } catch (error) {
      console.error('Erro ao carregar benefício acadêmico:', error);
    }
  };

  const submitBenefitApplication = async () => {
    if (benefitSubmitting) return;

    if (!benefitForm.institution.trim()) {
      showToast('Informe a instituição de ensino.', 'error');
      return;
    }

    if (!benefitForm.graduation_date) {
      showToast('Informe a data de conclusão da graduação.', 'error');
      return;
    }

    if (!benefitForm.proof_document) {
      showToast('Anexe um comprovante de conclusão da graduação.', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('benefit_type', 'academic');
    formData.append('subtype', benefitForm.subtype);
    formData.append('institution', benefitForm.institution);
    formData.append('course', benefitForm.course);
    formData.append('specialty', benefitForm.specialty);
    formData.append('expected_end_date', benefitForm.expected_end_date);
    formData.append('graduation_date', benefitForm.graduation_date);
    formData.append('notes', benefitForm.notes);
    formData.append('proof_document', benefitForm.proof_document);

    setBenefitSubmitting(true);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/benefits/apply`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      let data = {};

      try {
        data = await response.json();
      } catch (jsonError) {
        data = {};
      }

      if (!response.ok) {
        showToast(data.detail || 'Erro ao enviar solicitação.', 'error');
        return;
      }

      showToast('Solicitação enviada para análise.', 'success');
      setShowBenefitModal(false);
      setBenefitForm({
        subtype: 'new_graduate',
        institution: '',
        course: '',
        specialty: '',
        expected_end_date: '',
        graduation_date: '',
        notes: '',
        proof_document: null,
      });

      await fetchMyBenefit();
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);
      showToast(
        error?.message || 'Erro ao conectar com o servidor.',
        'error'
      );
    } finally {
      setBenefitSubmitting(false);
    }
  };
  
  const refreshSubscriptionStatus = async () => {
    setLoading(true);
    try {
      await fetchCurrentSubscription();
      await reloadAuthStatus?.();
      showToast('Status da assinatura atualizado.', 'success');
    } catch (error) {
      console.error('Erro ao atualizar status da assinatura:', error);
      showToast('Erro ao atualizar status da assinatura.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openSubscribeModal = (planId) => {
    const plan = plans.find((item) => item.id === planId);
    if (!plan) {
      showToast('Plano não encontrado', 'error');
      return;
    }
    setSelectedPlan(plan);
    setCouponCode('');
    setCouponValidation(null);
    setShowPaymentModal(true);
  };

  const openManageModal = () => {
    const plan = plans.find((item) => item.id === currentSubscription?.plan_id) || plans[0];
    if (!plan) {
      showToast('Plano não encontrado', 'error');
      return;
    }
    setSelectedPlan(plan);
    setBillingType(currentSubscription?.scheduled_billing_type || currentSubscription?.billing_type || 'monthly');
    setPaymentMethod(currentSubscription?.scheduled_payment_method || currentSubscription?.payment_method || 'credit_card');
    setShowPaymentModal(true);
  };

  const onlyDigits = (value) => String(value || '').replace(/\D/g, '');

  const formatPhoneBR = (value) => {
    const digits = onlyDigits(value).slice(0, 11);

    if (digits.length <= 2) return digits;
    if (digits.length <= 7) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    }

    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const validateCoupon = async () => {
    const code = couponCode.trim().toUpperCase();

    if (!code) {
      showToast('Informe um cupom para validar.', 'error');
      return;
    }

    setValidatingCoupon(true);
    setCouponValidation(null);

    try {
      const response = await fetch(`${backendUrl}/api/subscriptions/validate-coupon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      const result = await response.json();

      if (!response.ok || !result.valid) {
        setCouponValidation({ valid: false });
        showToast('Cupom inválido ou indisponível.', 'error');
        return;
      }

      setCouponCode(code);
      setCouponValidation(result);
      showToast('Cupom validado com sucesso.', 'success');
    } catch (error) {
      console.error('Erro ao validar cupom:', error);
      showToast('Erro ao validar cupom.', 'error');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const confirmSubscription = async () => {
    if (subscribing || !selectedPlan || !user?.id) return;
    if (!currentSubscription?.id) {
      if (!cpfCnpj.trim() || !postalCode.trim() || !address.trim() || !addressNumber.trim() || !province.trim() || !city.trim() || !state.trim() || !onlyDigits(billingPhone).trim()) {
        showToast('Preencha CPF/CNPJ, celular do pagador, CEP, endereço, número, bairro, cidade e UF.', 'error');
        return;
      }
    }

    setSubscribing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/subscriptions/subscribe`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          plan_id: selectedPlan.id,
          billing_type: billingType,
          cpf_cnpj: cpfCnpj,
          postal_code: postalCode,
          address,
          address_number: addressNumber,
          complement,
          province,
          city,
          state,
          billing_phone: onlyDigits(billingPhone).slice(0, 11),
          coupon_code: couponValidation?.valid ? couponCode.trim().toUpperCase() : undefined,
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        if (result?.reactivation_required && result?.subscription_id) {
          setShowPaymentModal(false);
          await fetchCurrentSubscription();
          showToast('Seu período de teste já foi usado. Reative a assinatura.', 'error');
          return;
        }
        showToast(result.detail || 'Erro ao processar assinatura', 'error');
        return;
      }

      setShowPaymentModal(false);
      await fetchCurrentSubscription();
      if (result.existing_subscription) {
        showToast('Você já possui uma assinatura em andamento.', 'success');
      } else {
        showToast(
          result.status === 'courtesy'
            ? 'Cortesia ativada com sucesso.'
            : 'Período de teste iniciado com sucesso.',
          'success'
        );
        await reloadAuthStatus?.();
      }
      document.getElementById('current-subscription')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      console.error('Erro ao criar assinatura:', error);
      showToast('Erro ao conectar com o servidor', 'error');
    } finally {
      setSubscribing(false);
    }
  };

  const activatePayment = async (subscriptionId, confirmBillingChange = false) => {
    if (!subscriptionId || activatingPayment) return;
    if (!cpfCnpj.trim()) {
      showToast('Preencha o CPF ou CNPJ do pagador.', 'error');
      return;
    }

    if (onlyDigits(billingPhone).length !== 11) {
      showToast('Informe um celular válido do pagador com DDD.', 'error');
      return;
    }

    setActivatingPayment(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/subscriptions/activate-payment/${subscriptionId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payment_method: paymentMethod,
          billing_type: billingType,
          confirm_billing_change: confirmBillingChange,
          cpf_cnpj: cpfCnpj,
          billing_phone: onlyDigits(billingPhone).slice(0, 11),
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        const detail = result?.detail;
        if (
          response.status === 409 &&
          detail?.code === 'BILLING_CYCLE_CHANGE_CONFIRMATION_REQUIRED'
        ) {
          setBillingChangeConfirmation(detail);
          return;
        }
        showToast(
          typeof detail === 'string' ? detail : 'Erro ao ativar cobrança',
          'error'
        );
        return;
      }

      setBillingChangeConfirmation(null);
      setShowPaymentModal(false);
      await fetchCurrentSubscription();

      if (result.invoice_url) {
        showToast(
          result.reused
            ? 'Cobrança existente recuperada. Conclua o pagamento no Asaas.'
            : 'Cobrança preparada com sucesso. Configure o pagamento no Asaas.',
          'success'
        );
        window.open(result.invoice_url, '_blank', 'noopener,noreferrer');
      } else {
        showToast('Assinatura reativada com sucesso.', 'success');
      }
      await reloadAuthStatus?.();
    } catch (error) {
      console.error('Erro ao ativar cobrança:', error);
      showToast('Erro ao ativar cobrança', 'error');
    } finally {
      setActivatingPayment(false);
    }
  };

  const manageActiveSubscription = async (subscriptionId, confirmBillingChange = false) => {
    if (!subscriptionId || managingSubscription) return;
    setManagingSubscription(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/subscriptions/manage/${subscriptionId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payment_method: paymentMethod,
          billing_type: billingType,
          confirm_billing_change: confirmBillingChange,
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        const detail = result?.detail;
        if (
          response.status === 409 &&
          detail?.code === 'BILLING_CYCLE_CHANGE_CONFIRMATION_REQUIRED'
        ) {
          setBillingChangeConfirmation(detail);
          return;
        }
        showToast(typeof detail === 'string' ? detail : 'Erro ao gerenciar assinatura', 'error');
        return;
      }
      setBillingChangeConfirmation(null);
      setShowPaymentModal(false);
      showToast(result.message || 'Assinatura atualizada.', 'success');
      if (result.requires_payment_configuration && result.invoice_url) {
        window.open(result.invoice_url, '_blank', 'noopener,noreferrer');
        showToast('Cadastre o cartão no Asaas para concluir a alteração.', 'success');
      }
      await fetchCurrentSubscription();
      await reloadAuthStatus?.();
    } catch (error) {
      console.error('Erro ao gerenciar assinatura:', error);
      showToast('Erro ao gerenciar assinatura', 'error');
    } finally {
      setManagingSubscription(false);
    }
  };

  const cancelScheduledChange = async () => {
    if (!currentSubscription?.id || managingSubscription) return;
    setManagingSubscription(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${backendUrl}/api/subscriptions/manage/${currentSubscription.id}/cancel-scheduled`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const result = await response.json();
      if (!response.ok) {
        showToast(result.detail || 'Erro ao cancelar mudança programada', 'error');
        return;
      }
      showToast(result.message, 'success');
      await fetchCurrentSubscription();
    } finally {
      setManagingSubscription(false);
    }
  };

  const submitPaymentChoice = (confirmBillingChange = false) => {
    if (currentSubscription?.status === 'active') {
      return manageActiveSubscription(currentSubscription.id, confirmBillingChange);
    }
    return activatePayment(currentSubscription?.id, confirmBillingChange);
  };

  const cancelSubscription = async () => {
    if (!currentSubscription?.id || cancelling) return;

    setCancelling(true);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/subscriptions/cancel/${currentSubscription.id}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showToast(data.detail || 'Erro ao cancelar assinatura', 'error');
        return;
      }

      showToast(data.message || 'Assinatura configurada para cancelamento.', 'success');

      await fetchCurrentSubscription();
      await reloadAuthStatus?.();
    } catch (error) {
      console.error(error);
      showToast('Erro ao cancelar assinatura', 'error');
    } finally {
      setCancelling(false);
    }
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price || 0);

  const getDaysRemaining = () => {
    if (!currentSubscription?.subscription_expires) return 0;
    const now = new Date();
    const expires = new Date(currentSubscription.subscription_expires);
    const diffTime = expires.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const benefitDaysRemaining = useMemo(() => {
    if (!myBenefit?.expires_at) return 0;

    const now = new Date();
    const expires = new Date(myBenefit.expires_at);
    const diffTime = expires.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  }, [myBenefit]);

  const benefitAlreadyUsed =
    ['approved', 'expired', 'rejected'].includes(myBenefit?.status);

  const benefitPending = myBenefit?.status === 'pending';

  const benefitButtonDisabled =
    benefitPending || benefitAlreadyUsed;

  const benefitButtonText =
    benefitPending
      ? 'Solicitação em análise'
      : myBenefit?.status === 'approved'
        ? 'Benefício ativo'
        : myBenefit?.status === 'expired'
          ? 'Benefício encerrado'
          : myBenefit?.status === 'rejected'
            ? 'Solicitação encerrada'
            : 'Solicitar benefício';

  const canShowBenefitCard =
    !benefitAlreadyUsed &&
    ['trial', 'inactive', undefined, null].includes(currentSubscription?.status);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'trial':
        return (
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4 text-blue-600" />
            <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
              Período de teste ({getDaysRemaining()} dias restantes)
            </span>
          </div>
        );
      case 'academic':
        return (
          <div className="flex items-center space-x-2">
            <GraduationCap className="w-4 h-4 text-indigo-600" />
            <span className="bg-indigo-100 text-indigo-800 text-sm font-medium px-3 py-1 rounded-full">
              Plano Acadêmico ativo ({getDaysRemaining()} dias restantes)
            </span>
          </div>
        );        
      case 'courtesy':
        return (
          <div className="flex items-center space-x-2">
            <Zap className="w-4 h-4 text-purple-600" />
            <span className="bg-purple-100 text-purple-800 text-sm font-medium px-3 py-1 rounded-full">
              {currentSubscription?.subscription_expires
                ? `Cortesia ativa (${getDaysRemaining()} dias restantes)`
                : 'Cortesia ativa por tempo indeterminado'}
            </span>
          </div>
        );        
      case 'active':
        return (
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
              Assinatura ativa
            </span>
          </div>
        );
      case 'pending_payment':
        return (
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <span className="bg-amber-100 text-amber-800 text-sm font-medium px-3 py-1 rounded-full">
              Aguardando pagamento
            </span>
          </div>
        );
      case 'payment_grace':
        return (
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <span className="bg-amber-100 text-amber-900 text-sm font-medium px-3 py-1 rounded-full">
              Pagamento em processamento
            </span>
          </div>
        );
      case 'suspended':
        return (
          <div className="flex items-center space-x-2">
            <Ban className="w-4 h-4 text-red-600" />
            <span className="bg-red-100 text-red-800 text-sm font-medium px-3 py-1 rounded-full">
              Assinatura suspensa
            </span>
          </div>
        );
      case 'blocked':
        return (
          <div className="flex items-center space-x-2">
            <Ban className="w-4 h-4 text-red-700" />
            <span className="bg-red-200 text-red-900 text-sm font-medium px-3 py-1 rounded-full">
              Usuário bloqueado
            </span>
          </div>
        );
      case 'canceled':
        return (
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 text-orange-600" />
            <span className="bg-orange-100 text-orange-800 text-sm font-medium px-3 py-1 rounded-full">
              Cancelamento agendado
            </span>
          </div>
        );
      default:
        return null;
    }
  };

  const canStartTrial =
    currentSubscription?.allow_new_trial === true ||
    currentSubscription?.status === 'inactive';

  const canReactivateSubscription =
    currentSubscription?.can_reactivate === true ||
    ['suspended', 'canceled'].includes(currentSubscription?.status);
  const canShowActivationBox =
    ['trial', 'academic', 'courtesy', 'pending_payment', 'payment_grace', 'suspended', 'canceled'].includes(
      currentSubscription?.status
    ) ||
    currentSubscription?.can_reactivate === true;
  const primaryButtonText = !currentSubscription?.id
    ? 'Iniciar teste grátis'
    : currentSubscription?.status === 'trial'
      ? 'Assinar agora'
      : currentSubscription?.status === 'suspended'
        ? 'Reativar assinatura'
        : currentSubscription?.status === 'canceled'
          ? 'Reativar assinatura'
          : 'Gerenciar assinatura';

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-b-purple-600"></div>
        </div>
      </Layout>
    );
  }

  if (currentSubscription?.status === 'blocked') {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-10">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
            <Ban className="w-14 h-14 text-red-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-red-900 mb-3">Acesso bloqueado</h1>
            <p className="text-red-800">
              Seu acesso foi bloqueado pelo administrador. Entre em contato com o suporte para regularização.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Assinatura do OdontoAssist AI</h1>
          <p className="text-xl text-gray-600 mb-8">
            Gestão odontológica com prontuário, agenda, odontograma e apoio de IA para anamnese.
          </p>

          {canStartTrial && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 inline-block">
              <div className="flex items-center">
                <Zap className="w-5 h-5 text-blue-600 mr-2" />
                <p className="text-blue-800 font-medium">Novo usuário recebe 7 dias grátis automaticamente.</p>
              </div>
            </div>
          )}


        </div>

        {(currentSubscription?.id || canShowActivationBox) && (
          <div
            id="current-subscription"
            className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-6 mb-8"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Sua assinatura atual
                  </h3>

                  <button
                    type="button"
                    onClick={refreshSubscriptionStatus}
                    className="text-xs px-3 py-1.5 rounded-lg border border-purple-200 text-purple-700 hover:bg-purple-50"
                  >
                    Atualizar status
                  </button>
                </div>
                <div className="space-y-2">
                  <p className="text-gray-700">
                    <strong>Plano:</strong> {currentPlanDisplayName}
                  </p>
                  <p className="text-gray-700">
                    <strong>Periodicidade:</strong>{' '}
                    {currentSubscription?.billing_type === 'yearly'
                      ? 'Anual'
                      : 'Mensal'}
                  </p>                  
                  {hasFounderPrice && (
                    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                      <p className="font-bold text-amber-900">Preço de Fundador preservado</p>
                      <p className="mt-1 text-sm text-amber-800">
                        Seu valor promocional permanece garantido enquanto a assinatura continuar ativa.
                      </p>
                      <p className="mt-2 text-sm font-bold text-amber-900">
                        Mensal: {formatPrice(currentEffectiveMonthlyPrice)}
                        {' '}•{' '}
                        Anual: {formatPrice(currentEffectiveYearlyPrice)}
                      </p>
                    </div>
                  )}
                  {getStatusBadge(currentSubscription.status)}
                  {currentSubscription.status === 'payment_grace' && (
                    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                      <p className="font-semibold">Seu pagamento ainda não foi confirmado.</p>
                      <p className="mt-1">
                        O acesso permanece liberado até o fim de{' '}
                        {new Date(
                          currentSubscription.payment_grace_until || currentSubscription.subscription_expires
                        ).toLocaleDateString('pt-BR')}. Verifique ou atualize o pagamento para evitar a suspensão.
                      </p>
                    </div>
                  )}
                  {currentSubscription.subscription_expires && (
                    <p className="text-gray-700">
                      <strong>
                        {currentSubscription.status === 'trial'
                          ? 'Trial expira em:'
                          : currentSubscription.status === 'courtesy'
                            ? 'Cortesia expira em:'
                            : currentSubscription.status === 'academic'
                              ? 'Benefício acadêmico expira em:'
                              : currentSubscription.status === 'payment_grace'
                                ? 'Carência de pagamento até:'
                              : 'Próximo vencimento / acesso até:'}
                      </strong>{' '}
                      {new Date(currentSubscription.subscription_expires).toLocaleDateString('pt-BR')}
                    </p>
                  )}
                  {currentSubscription.gateway_invoice_url && (
                    <div className="flex flex-col items-start gap-3 mt-4 mb-2">
                      <div className="flex flex-wrap gap-3">
                        <a
                          href={currentSubscription.gateway_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center border border-purple-200 text-purple-700 px-5 py-2.5 rounded-lg hover:bg-purple-50 transition-all font-semibold"
                        >
                          {currentSubscription.status === 'active'
                            ? 'Ver pagamento e comprovante'
                            : 'Continuar pagamento no Asaas'}
                          <ExternalLink className="w-4 h-4 ml-2" />
                        </a>
                        {currentSubscription.status === 'active' && (
                          <button
                            type="button"
                            onClick={openManageModal}
                            className="inline-flex items-center justify-center bg-gradient-to-r from-purple-600 to-blue-600 text-white px-5 py-2.5 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all font-semibold shadow-md"
                          >
                            Gerenciar assinatura
                          </button>
                        )}
                      </div>
                      
                      {(currentSubscription.status === 'trial' || currentSubscription.status === 'pending_payment') && (
                        <p className="text-xs text-gray-500 mt-1 max-w-sm">
                          Configure a forma de pagamento (Cartão ou Boleto) em até 7 dias e evite a suspensão do acesso.
                        </p>
                      )}
                    </div>
                  )}
                  {currentSubscription.status === 'active' && !currentSubscription.gateway_invoice_url && (
                    <button
                      type="button"
                      onClick={openManageModal}
                      className="mt-4 inline-flex items-center justify-center bg-gradient-to-r from-purple-600 to-blue-600 text-white px-5 py-2.5 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all font-semibold shadow-md"
                    >
                      Gerenciar assinatura
                    </button>
                  )}
                  {currentSubscription?.scheduled_billing_type && (
                    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                      <p className="font-bold">Mudança programada</p>
                      <p className="mt-1">
                        Na próxima renovação, o plano será{' '}
                        <strong>{currentSubscription.scheduled_billing_type === 'yearly' ? 'anual' : 'mensal'}</strong>
                        {' '}por{' '}
                        <strong>{currentSubscription.scheduled_payment_method === 'boleto' ? 'boleto/PIX' : 'cartão'}</strong>.
                      </p>
                      {currentSubscription.scheduled_change_effective_at && (
                        <p className="mt-1">
                          Prevista para{' '}
                          {new Date(currentSubscription.scheduled_change_effective_at).toLocaleDateString('pt-BR')}.
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={cancelScheduledChange}
                        disabled={managingSubscription}
                        className="mt-3 font-semibold text-amber-900 underline disabled:opacity-50"
                      >
                        Cancelar mudança programada
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {canShowActivationBox && (
                <div className="w-full lg:w-[360px] bg-white rounded-xl border border-purple-100 p-4 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    {currentSubscription?.status === 'pending_payment'
                      ? 'Pagamento em andamento'
                      : ['trial', 'academic'].includes(currentSubscription?.status)
                      ? 'Ativar assinatura'
                      : 'Reativar assinatura'}
                  </h4>

                  <h4 className="font-medium text-sm text-gray-700 mb-2 mt-4">Dados do Pagador</h4>
                  <input
                    type="text"
                    value={cpfCnpj}
                    onChange={(event) => setCpfCnpj(event.target.value)}
                    placeholder="CPF ou CNPJ"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />

                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatPhoneBR(billingPhone)}
                    onChange={(e) => {
                      const digits = onlyDigits(e.target.value).slice(0, 11);
                      setBillingPhone(digits);
                    }}
                    placeholder="Celular do pagador com DDD"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />

                  <h4 className="font-medium text-sm text-gray-700 mb-2">
                    Periodicidade
                  </h4>

                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => setBillingType('monthly')}
                      className={`p-2 rounded-lg border-2 text-sm font-semibold transition-all ${
                        billingType === 'monthly'
                          ? 'border-purple-600 bg-purple-50 text-purple-700'
                          : 'border-gray-200 bg-white text-gray-600'
                      }`}
                    >
                      Mensal
                      <span className="block text-xs font-normal mt-1">
                        {formatPrice(currentEffectiveMonthlyPrice)}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setBillingType('yearly')}
                      className={`p-2 rounded-lg border-2 text-sm font-semibold transition-all ${
                        billingType === 'yearly'
                          ? 'border-purple-600 bg-purple-50 text-purple-700'
                          : 'border-gray-200 bg-white text-gray-600'
                      }`}
                    >
                      Anual
                      <span className="block text-xs font-normal mt-1">
                        {formatPrice(currentEffectiveYearlyPrice)}
                      </span>
                    </button>
                  </div>

                  {(isBillingCycleChange || isPaymentMethodChange) && (
                    <div className={`mb-4 rounded-lg border p-3 text-xs ${
                      isBillingCycleChange
                        ? 'border-amber-300 bg-amber-50 text-amber-900'
                        : 'border-blue-200 bg-blue-50 text-blue-800'
                    }`}>
                      {isBillingCycleChange
                        ? 'Você está alterando a periodicidade. Antes de substituir a recorrência atual, o sistema solicitará uma confirmação.'
                        : 'A periodicidade será mantida. Somente a forma de pagamento da recorrência atual será alterada.'}
                    </div>
                  )}

                  <h4 className="font-medium text-sm text-gray-700 mb-2">Forma de Pagamento Preferida</h4>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <button
                      type="button"
                      className={`flex flex-col items-center justify-center p-2 rounded-lg border-2 transition-all ${
                        paymentMethod === 'credit_card' ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-purple-300 bg-white'
                      }`}
                      onClick={() => setPaymentMethod('credit_card')}
                    >
                      <CreditCard className="w-5 h-5 mb-1" />
                      <span className="font-semibold text-xs">Cartão</span>
                    </button>
                    <button
                      type="button"
                      className={`flex flex-col items-center justify-center p-2 rounded-lg border-2 transition-all ${
                        paymentMethod === 'boleto' ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-purple-300 bg-white'
                      }`}
                      onClick={() => setPaymentMethod('boleto')}
                    >
                      <Receipt className="w-5 h-5 mb-1" />
                      <span className="font-semibold text-xs">Boleto</span>
                    </button>
                  </div>

                  <button
                    onClick={() => {
                      if (!currentSubscription?.id) {
                        showToast('Não foi encontrada uma assinatura anterior para reativar. Entre em contato com o suporte.', 'error');
                        return;
                      }

                      if (currentSubscription?.status === 'academic') {
                        setShowAcademicActivationModal(true);
                        return;
                      }

                      activatePayment(currentSubscription.id);
                    }}
                    disabled={activatingPayment}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg disabled:opacity-50"
                  >
                    {activatingPayment
                      ? 'Processando...'
                      : currentSubscription?.status === 'pending_payment'
                        ? 'Continuar ou alterar pagamento'
                      : currentSubscription?.status === 'academic'
                        ? getDaysRemaining() <= 30
                          ? 'Ativar assinatura'
                          : 'Ativar assinatura antecipadamente'
                        : currentSubscription?.status === 'trial'
                          ? 'Ativar assinatura'
                          : 'Reativar assinatura'}
                  </button>

                  {currentSubscription?.status === 'academic' && (
                    <p className="text-xs text-indigo-700 mt-2">
                      Ao ativar uma assinatura paga, você poderá contratar módulos adicionais, como o WhatsApp Business.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {canShowBenefitCard && (
        <div className="bg-white border border-indigo-100 rounded-xl p-6 mb-8 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-indigo-700" />
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Programa de Benefícios para Recém-Formados
                </h3>

                <p className="text-gray-600 mt-1">
                  Benefício gratuito de 90 dias, concedido uma única vez, para
                  cirurgiões-dentistas que concluíram a graduação há até 12 meses,
                  mediante análise da documentação.
                </p>

                <Link
                  to="/ajuda/tutoriais/programa-beneficios"
                  className="inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-800 mt-3"
                >
                  Conheça as regras do Programa de Benefícios
                  <ExternalLink className="w-3.5 h-3.5 ml-1" />
                </Link>

                {myBenefit?.status && myBenefit.status !== 'none' && (
                  <div className="mt-4 text-sm space-y-1">
                    <p className="text-gray-700">
                      <strong>Status:</strong>{' '}
                      {myBenefit.status === 'pending'
                        ? 'Em análise'
                        : myBenefit.status === 'approved'
                          ? 'Aprovado'
                          : myBenefit.status === 'rejected'
                            ? 'Reprovado'
                            : myBenefit.status === 'expired'
                              ? 'Expirado'
                              : myBenefit.status}
                    </p>

                    <p className="text-gray-700">
                      <strong>Programa:</strong>{' '}
                      {myBenefit.subtype === 'postgraduate'
                        ? 'Pós-graduação'
                        : myBenefit.subtype === 'new_graduate'
                          ? 'Recém-formado'
                          : myBenefit.benefit_type_name || 'Programa de Benefícios'}
                    </p>

                    {myBenefit.expires_at && (
                      <p className="text-gray-700">
                        <strong>Válido até:</strong>{' '}
                        {new Date(myBenefit.expires_at).toLocaleDateString('pt-BR')}{' '}
                        <span className="text-indigo-700 font-medium">
                          ({benefitDaysRemaining} dias restantes)
                        </span>
                      </p>
                    )}

                    {myBenefit.institution && (
                      <p className="text-gray-700">
                        <strong>Instituição:</strong> {myBenefit.institution}
                      </p>
                    )}

                    {myBenefit.status === 'approved' && myBenefit.subtype === 'postgraduate' && (
                      <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
                        Este benefício é concedido uma única vez por 90 dias para alunos de pós-graduação em Odontologia.
                      </div>
                    )}

                    {myBenefit.status === 'approved' && myBenefit.subtype === 'new_graduate' && (
                      <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                        Este benefício é concedido uma única vez por 90 dias para recém-formados em Odontologia, desde que a graduação tenha sido concluída há até 12 meses.
                      </div>
                    )}

                    {Array.isArray(myBenefit.history) && myBenefit.history.length > 0 && (
                      <div className="mt-5 border-t border-gray-100 pt-4">
                        <h4 className="font-semibold text-gray-900 mb-3">
                          Histórico do benefício
                        </h4>

                        <div className="space-y-3">
                          {myBenefit.history
                            .slice()
                            .reverse()
                            .map((event, index) => (
                              <div key={`${event.status || 'event'}-${event.at || index}`} className="flex gap-3">
                                <div className="mt-1 w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" />

                                <div>
                                  <p className="text-sm text-gray-800">
                                    {event.status === 'pending'
                                      ? 'Solicitação enviada'
                                      : event.status === 'approved'
                                        ? 'Benefício aprovado'
                                        : event.status === 'rejected'
                                          ? 'Solicitação reprovada'
                                          : event.status === 'expired'
                                            ? 'Benefício expirado'
                                            : event.status || 'Atualização'}
                                  </p>

                                  {event.note && (
                                    <p className="text-xs text-gray-500">
                                      {event.note}
                                    </p>
                                  )}

                                  {event.at && (
                                    <p className="text-xs text-gray-400">
                                      {new Date(event.at).toLocaleString('pt-BR')}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="w-full lg:w-auto">
              <button
                type="button"
                onClick={() => setShowBenefitModal(true)}
                disabled={benefitButtonDisabled}
                className="w-full lg:w-auto inline-flex items-center justify-center bg-indigo-600 text-white px-5 py-3 rounded-lg hover:bg-indigo-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <GraduationCap className="w-4 h-4 mr-2" />
                {benefitButtonText}
              </button>

            </div>
          </div>
        </div>
        )}

        <div className="max-w-md mx-auto mb-10">
          <div className="bg-gray-100 rounded-2xl p-1.5 grid grid-cols-2">
            <button
              type="button"
              onClick={() => setBillingType('monthly')}
              className={`rounded-xl px-5 py-3 font-semibold text-sm transition-all ${
                billingType === 'monthly'
                  ? 'bg-white text-purple-700 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Mensal
            </button>

            <button
              type="button"
              onClick={() => setBillingType('yearly')}
              className={`rounded-xl px-5 py-3 font-semibold text-sm transition-all ${
                billingType === 'yearly'
                  ? 'bg-white text-purple-700 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Anual
              <span className="block text-xs font-normal mt-0.5 text-green-700">
                Economize 2 mensalidades
              </span>
            </button>
          </div>
        </div>

        <div className={`grid grid-cols-1 ${singlePlanMode ? 'md:grid-cols-1' : 'md:grid-cols-3'} gap-8 mb-12`}>
          {plans.map((plan) => {
            const price =
              billingType === 'monthly'
                ? plan.price_monthly
                : plan.price_yearly;

            const annualEquivalentMonthly =
              plan.price_yearly
                ? plan.price_yearly / 12
                : 0;

            const annualSavings =
              plan.price_monthly && plan.price_yearly
                ? plan.price_monthly * 12 - plan.price_yearly
                : 0;

            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl shadow-lg border-2 p-8 transition-all hover:shadow-xl ${
                  singlePlanMode ? 'border-purple-500 max-w-2xl mx-auto w-full' : 'border-gray-200 hover:border-purple-300'
                }`}
              >

                <div className="text-center mb-8 mt-4">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-6">{plan.description}</p>



                  <div className="mb-4">
                    <span className="text-4xl font-bold text-gray-900">
                      {formatPrice(price)}
                    </span>

                    <span className="text-gray-600 ml-2 text-lg">
                      {billingType === 'monthly' ? '/mês' : '/ano'}
                    </span>
                  </div>

                  {billingType === 'yearly' && (
                    <div className="mb-6">
                      <p className="text-purple-700 font-semibold">
                        Equivale a {formatPrice(annualEquivalentMonthly)} por mês
                      </p>

                      <p className="text-sm text-green-700 mt-1">
                        Economia de {formatPrice(annualSavings)} por ano
                      </p>
                    </div>
                  )}


                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex items-center">
                    <Users className="w-5 h-5 text-purple-600 mr-3" />
                    <span className="text-gray-700">Até {plan.max_users} usuários</span>
                  </div>
                  <div className="flex items-center">
                    <Database className="w-5 h-5 text-purple-600 mr-3" />
                    <span className="text-gray-700">Até {plan.max_patients} pacientes</span>
                  </div>

                  {Array.isArray(plan.features) &&
                    plan.features.map((feature) => (
                      <div key={feature} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                </div>

                {canStartTrial && (
                  <button
                    onClick={() => openSubscribeModal(plan.id)}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg"
                  >
                    Iniciar teste grátis
                  </button>
                )}

                {canReactivateSubscription && (
                  <div className="text-center text-sm text-gray-500">
                    Use o card de reativação acima para gerar a cobrança.
                  </div>
                )}

                {currentSubscription?.id && ['active', 'pending_payment', 'payment_grace', 'trial', 'canceled'].includes(currentSubscription.status) && (
                  <div className="text-center text-sm text-gray-500">
                    {primaryButtonText}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {currentSubscription?.can_cancel && (
          <div className="max-w-2xl mx-auto mb-12">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Cancelar assinatura</h3>
              <p className="text-gray-600 mb-4">
                Ao cancelar, sua renovação é interrompida, mas o acesso permanece até o fim do período já pago.
              </p>
              <button
                onClick={() => setShowCancelModal(true)}
                disabled={cancelling}
                className="bg-red-600 text-white px-5 py-3 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {cancelling ? 'Cancelando...' : 'Cancelar renovação'}
              </button>
            </div>
          </div>
        )}

        {showCancelModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900">
                  Cancelar renovação da assinatura?
                </h2>
                <p className="text-gray-600 mt-2">
                  Ao cancelar, sua renovação será interrompida no Asaas, mas o acesso permanecerá ativo até o fim do período já contratado.
                </p>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-sm text-amber-900">
                    Se o Módulo WhatsApp Business estiver contratado, a renovação dele também será cancelada automaticamente, pois o módulo depende da assinatura principal.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCancelModal(false)}
                    className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50"
                  >
                    Manter assinatura
                  </button>

                  <button
                    type="button"
                    onClick={async () => {
                      await cancelSubscription();
                      setShowCancelModal(false);
                    }}
                    disabled={cancelling}
                    className="flex-1 bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50"
                  >
                    {cancelling ? 'Cancelando...' : 'Confirmar cancelamento'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showPaymentModal && selectedPlan && (
          <div className="fixed inset-0 bg-black/60 z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl mx-auto my-6 max-h-[calc(100vh-3rem)] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900">
                  {!currentSubscription?.id
                    ? 'Iniciar teste grátis'
                    : currentSubscription?.status === 'active'
                      ? 'Gerenciar assinatura'
                      : 'Reativar assinatura'}
                </h2>
                <p className="text-gray-600 mt-2">
                  {!currentSubscription?.id
                    ? 'Escolha a periodicidade desejada. Seu período de teste será iniciado automaticamente uma única vez.'
                    : currentSubscription?.status === 'active'
                      ? 'Altere como será feita a próxima renovação. O período já pago será preservado.'
                    : currentSubscription?.status === 'trial'
                      ? 'Escolha a periodicidade e a forma de pagamento para ativar sua assinatura.'
                      : 'Escolha a periodicidade e a forma de pagamento para reativar sua assinatura.'}
                </p>
              </div>

              <div className="p-6">
                <div className="bg-purple-50 rounded-xl p-4 mb-6">
                  <div className="font-semibold text-gray-900">
                    {selectedPlan.name}
                  </div>

                  <div className="text-sm text-gray-600 mt-1">
                    {selectedPlan.description}
                  </div>

                  <div className="text-2xl font-bold text-purple-700 mt-3">
                    {formatPrice(
                      billingType === 'monthly'
                        ? (hasFounderPrice ? currentEffectiveMonthlyPrice : selectedPlan.price_monthly)
                        : (hasFounderPrice ? currentEffectiveYearlyPrice : selectedPlan.price_yearly)
                    )}

                    <span className="text-sm text-gray-500 ml-2">
                      {billingType === 'monthly' ? '/mês' : '/ano'}
                    </span>
                  </div>

                  <div className="mt-4 inline-flex rounded-lg bg-white border border-purple-200 p-1">
                    <button
                      type="button"
                      onClick={() => setBillingType('monthly')}
                      className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                        billingType === 'monthly'
                          ? 'bg-purple-600 text-white'
                          : 'text-gray-600 hover:text-purple-700'
                      }`}
                    >
                      Mensal
                    </button>

                    <button
                      type="button"
                      onClick={() => setBillingType('yearly')}
                      className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                        billingType === 'yearly'
                          ? 'bg-purple-600 text-white'
                          : 'text-gray-600 hover:text-purple-700'
                      }`}
                    >
                      Anual
                    </button>
                  </div>

                  {billingType === 'yearly' && (
                    <div className="mt-3">
                      <p className="text-sm font-semibold text-green-700">
                        Equivale a{' '}
                        {formatPrice((hasFounderPrice ? currentEffectiveYearlyPrice : selectedPlan.price_yearly) / 12)} por mês
                      </p>

                      <p className="text-xs text-green-700 mt-1">
                        Cobrança anual recorrente com economia equivalente a aproximadamente
                        duas mensalidades.
                      </p>
                    </div>
                  )}
                </div>


                {!currentSubscription?.id && (
                  <div className="border border-purple-100 rounded-xl p-4 mb-6 bg-white">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cupom de indicação (opcional)
                    </label>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(event) => {
                          setCouponCode(event.target.value.toUpperCase());
                          setCouponValidation(null);
                        }}
                        placeholder="Digite seu cupom"
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      />

                      <button
                        type="button"
                        onClick={validateCoupon}
                        disabled={validatingCoupon}
                        className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
                      >
                        {validatingCoupon ? 'Validando...' : 'Validar'}
                      </button>
                    </div>

                    {couponValidation?.valid && (
                      <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                        <strong>Cupom válido.</strong>{' '}
                        {couponValidation.description || 'Cupom de indicação aplicado.'}
                        <br />
                        Você receberá {couponValidation.courtesy_days || 30} dias de acesso gratuito.
                      </div>
                    )}

                    {couponValidation && !couponValidation.valid && (
                      <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                        Cupom inválido ou indisponível.
                      </div>
                    )}
                  </div>
                )}

                {!currentSubscription?.id && (
                  <div className="space-y-3 mb-5 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h3 className="font-semibold text-gray-800 text-sm">Dados cadastrais para ativação do teste</h3>
                    
                    <input
                      type="text"
                      value={cpfCnpj}
                      onChange={(e) => setCpfCnpj(e.target.value)}
                      placeholder="CPF ou CNPJ (apenas números)"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />

                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatPhoneBR(billingPhone)}
                      onChange={(e) => {
                        const digits = onlyDigits(e.target.value).slice(0, 11);
                        setBillingPhone(digits);
                      }}
                      placeholder="Celular do pagador com DDD"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />

                    <div className="grid grid-cols-[1fr_2fr] gap-3">
                      <input
                        type="text"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        onBlur={handleCepBlur}
                        placeholder="CEP"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <input
                        type="text"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Endereço (Rua, Av.)"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div className="grid grid-cols-[1fr_2fr] gap-3">
                      <input
                        type="text"
                        value={addressNumber}
                        onChange={(e) => setAddressNumber(e.target.value)}
                        placeholder="Número"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <input
                        type="text"
                        value={complement}
                        onChange={(e) => setComplement(e.target.value)}
                        placeholder="Complemento"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div className="grid grid-cols-[2fr_2fr_1fr] gap-3">
                      <input
                        type="text"
                        value={province}
                        onChange={(e) => setProvince(e.target.value)}
                        placeholder="Bairro"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <input
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="Cidade"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <input
                        type="text"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        placeholder="UF"
                        maxLength={2}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 uppercase"
                      />
                    </div>
                  </div>
                )}

                {currentSubscription?.id && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">
                      Forma de Pagamento Preferida
                    </h3>
                    <p className="text-xs text-gray-500 mb-3">
                      {currentSubscription?.status === 'active'
                        ? 'Escolha como deseja pagar a próxima renovação.'
                        : 'Escolha a forma que deseja utilizar para ativar ou reativar a cobrança recorrente.'}
                    </p>
                    <div className="grid grid-cols-2 gap-3 max-w-sm">
                      <button
                        type="button"
                        className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${
                          paymentMethod === 'credit_card'
                            ? 'border-purple-600 bg-purple-50 text-purple-700'
                            : 'border-gray-200 hover:border-purple-300 bg-white'
                        }`}
                        onClick={() => setPaymentMethod('credit_card')}
                      >
                        <CreditCard className="w-6 h-6 mb-2" />
                        <span className="font-semibold text-sm">Cartão de Crédito</span>
                      </button>
                      <button
                        type="button"
                        className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${
                          paymentMethod === 'boleto'
                            ? 'border-purple-600 bg-purple-50 text-purple-700'
                            : 'border-gray-200 hover:border-purple-300 bg-white'
                        }`}
                        onClick={() => setPaymentMethod('boleto')}
                      >
                        <Receipt className="w-6 h-6 mb-2" />
                        <span className="font-semibold text-sm">Boleto Bancário</span>
                      </button>
                    </div>
                    {(isBillingCycleChange || isPaymentMethodChange) && (
                      <div className={`mt-4 rounded-lg border p-3 text-sm ${
                        isBillingCycleChange
                          ? 'border-amber-300 bg-amber-50 text-amber-900'
                          : 'border-blue-200 bg-blue-50 text-blue-800'
                      }`}>
                        {isBillingCycleChange
                          ? 'A periodicidade atual será substituída somente após sua confirmação.'
                          : 'A assinatura continuará na mesma periodicidade; apenas a forma de pagamento será atualizada.'}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50"
                  >
                    Fechar
                  </button>
                  {!currentSubscription?.id ? (
                    <button
                      onClick={confirmSubscription}
                      disabled={subscribing}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 disabled:opacity-50"
                    >
                      {subscribing
                        ? 'Processando...'
                        : couponValidation?.valid
                          ? 'Ativar cortesia'
                          : 'Iniciar Período de Testes'}
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        if (currentSubscription?.id) {
                          if (currentSubscription?.status === 'academic') {
                            setShowAcademicActivationModal(true);
                            return;
                          }

                          submitPaymentChoice();
                        }
                      }}
                      disabled={activatingPayment || managingSubscription}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 disabled:opacity-50"
                    >
                      {(activatingPayment || managingSubscription)
                        ? 'Processando...'
                        : currentSubscription?.status === 'active'
                          ? 'Salvar próximas cobranças'
                        : currentSubscription?.status === 'academic'
                          ? getDaysRemaining() <= 30
                            ? 'Ativar assinatura'
                            : 'Ativar assinatura antecipadamente'
                          : 'Reativar assinatura'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
        {showBenefitModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between border-b px-6 py-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Solicitar benefício para recém-formado
                  </h3>

                  <p className="text-sm text-gray-600">
                    Disponível para cirurgiões-dentistas que concluíram a graduação há
                    até 12 meses.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setShowBenefitModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <GraduationCap className="w-5 h-5 text-indigo-700 mt-0.5 flex-shrink-0" />

                    <div>
                      <strong className="block text-indigo-900">
                        Benefício para recém-formados
                      </strong>

                      <span className="text-sm text-indigo-800">
                        Concede 90 dias gratuitos, uma única vez, para profissionais
                        formados em Odontologia há até 12 meses.
                      </span>
                    </div>
                  </div>
                </div>

                <input
                  type="text"
                  value={benefitForm.institution}
                  onChange={(e) => setBenefitForm((prev) => ({ ...prev, institution: e.target.value }))}
                  placeholder="Instituição de ensino"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />

                <input
                  type="text"
                  value={benefitForm.course}
                  onChange={(e) =>
                    setBenefitForm((prev) => ({
                      ...prev,
                      course: e.target.value,
                    }))
                  }
                  placeholder="Curso de graduação, por exemplo: Odontologia"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de conclusão da graduação
                  </label>

                  <input
                    type="date"
                    value={benefitForm.graduation_date}
                    onChange={(e) =>
                      setBenefitForm((prev) => ({
                        ...prev,
                        graduation_date: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />

                  <p className="text-xs text-gray-500 mt-1">
                    A graduação deve ter sido concluída há no máximo 12 meses.
                  </p>
                </div>

                <textarea
                  value={benefitForm.notes}
                  onChange={(e) => setBenefitForm((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Observações adicionais"
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />

                <label className="block border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-indigo-400">
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm font-medium text-gray-700">
                    {benefitForm.proof_document
                      ? benefitForm.proof_document.name
                      : 'Clique para anexar o comprovante'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Anexe diploma, certificado, declaração de conclusão ou documento
                    equivalente. Formatos PDF, JPG, PNG ou WEBP, com até 5MB.
                  </p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setBenefitForm((prev) => ({ ...prev, proof_document: file }));
                    }}
                  />
                </label>
              </div>

              <div className="border-t px-6 py-4 flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowBenefitModal(false)}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  onClick={submitBenefitApplication}
                  disabled={benefitSubmitting}
                  className="px-5 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {benefitSubmitting ? 'Enviando...' : 'Enviar solicitação'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showAcademicActivationModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Ativar assinatura antecipadamente?
              </h3>

              <p className="text-gray-700 mb-3">
                Seu benefício do Programa de Benefícios ainda está válido até{' '}
                <strong>
                  {currentSubscription?.subscription_expires
                    ? new Date(currentSubscription.subscription_expires).toLocaleDateString('pt-BR')
                    : 'a data prevista'}
                </strong>.
              </p>

              <p className="text-gray-700 mb-3">
                Não é necessário ativar a assinatura agora para continuar usando a plataforma.
              </p>

              <p className="text-gray-700 mb-4">
                Ative antecipadamente apenas se desejar contratar módulos adicionais,
                como o WhatsApp Business, ou se quiser iniciar a assinatura paga antes do fim do benefício.
              </p>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAcademicActivationModal(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowAcademicActivationModal(false);
                    activatePayment(currentSubscription.id);
                  }}
                  className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700"
                >
                  Sim, ativar assinatura
                </button>
              </div>
            </div>
          </div>
        )}

        {billingChangeConfirmation && currentSubscription?.id && (
          <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-amber-100 p-2">
                  <AlertCircle className="w-6 h-6 text-amber-700" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Confirmar mudança de periodicidade
                  </h3>
                  <p className="mt-2 text-gray-700">
                    {billingChangeConfirmation.message}
                  </p>
                </div>
              </div>

              <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <p>
                  <strong>Atual:</strong>{' '}
                  {billingChangeConfirmation.current_billing_type === 'yearly' ? 'Anual' : 'Mensal'}
                </p>
                <p className="mt-1">
                  <strong>Nova opção:</strong>{' '}
                  {billingChangeConfirmation.requested_billing_type === 'yearly' ? 'Anual' : 'Mensal'}
                </p>
                {billingChangeConfirmation.next_amount != null && (
                  <p className="mt-1">
                    <strong>Próximo valor:</strong>{' '}
                    {formatPrice(billingChangeConfirmation.next_amount)}
                  </p>
                )}
                {billingChangeConfirmation.effective_at && (
                  <p className="mt-1">
                    <strong>Próxima renovação:</strong>{' '}
                    {new Date(billingChangeConfirmation.effective_at).toLocaleDateString('pt-BR')}
                  </p>
                )}
                <p className="mt-3">
                  {currentSubscription.status === 'active'
                    ? 'O período já pago será preservado. A nova periodicidade valerá somente na próxima renovação e nenhuma cobrança será feita agora.'
                    : 'A cobrança anterior pendente será cancelada. Depois da substituição, permanecerá somente uma recorrência ativa.'}
                </p>
              </div>

              <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setBillingChangeConfirmation(null)}
                  disabled={activatingPayment}
                  className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Manter opção atual
                </button>
                <button
                  type="button"
                  onClick={() => submitPaymentChoice(true)}
                  disabled={activatingPayment || managingSubscription}
                  className="px-5 py-2.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {(activatingPayment || managingSubscription) ? 'Salvando...' : 'Confirmar mudança'}
                </button>
              </div>
            </div>
          </div>
        )}

    </Layout>
  );
};

export default SubscriptionPage;
